import moment from 'moment';
import { Bloodwork, BloodworkAnalysis, PanelMarker, Section } from '../models';
import { SECTION_PANEL_MARKER_GETTERS } from '../models/BloodworkAnalysis';

/** Pulls one section's markers off an analysis (e.g. `a => a.getRBCPanelMarkers()`). */
export type SectionMarkerGetter = (analysis: BloodworkAnalysis) => PanelMarker[];

/** Appends a section's previous-only markers to its current ones. */
export type WithPreviousOnly = (
  currentMarkers: PanelMarker[],
  getPreviousSectionMarkers: SectionMarkerGetter,
) => PanelMarker[];

/**
 * When comparing to a previous test, append that section's "previous-only"
 * markers — the ones with a value last time and none now — so their previous
 * (blue) blood drop still gets a bar graph. Gated on the practitioner's
 * showPreviousTestMarkersInComparison setting and on a previous test existing.
 *
 * A section whose CURRENT test has no markers at all is augmented like any
 * other: the whole section is built from previous-only markers and renders as
 * bar graphs showing only the previous drops. Skipping the empty case (which we
 * used to do) meant a section the client had results for last time but not this
 * time vanished from the report entirely, which read as the app losing the data
 * — the comparison table shows those markers, so the analysis has to as well.
 *
 * Such a section carries no current values, so anything describing the CURRENT
 * test stays out of it: its insights panel is rebuilt from the previous
 * collection and labeled with that date instead (see insightsSourceAnalysis),
 * and it has no Results Overview pill or Markers Outside Optimal entry (those
 * read the raw current-only getters).
 *
 * Callers must have already run preparePreviousPanelMarkers() on the current
 * analysis — getPreviousOnlyPanelMarkers() reads the previousPanelMarker links
 * it hangs.
 */
export function makeWithPreviousOnly(
  bloodworkAnalysis: BloodworkAnalysis,
  previousBloodworkAnalysis: BloodworkAnalysis | undefined,
  showPreviousTestMarkersInComparison: boolean | undefined,
): WithPreviousOnly {
  const showPreviousOnly =
    (showPreviousTestMarkersInComparison ?? true) && !!previousBloodworkAnalysis;
  return (currentMarkers, getPreviousSectionMarkers) => {
    if (!showPreviousOnly) {
      return currentMarkers;
    }
    const previousOnly = bloodworkAnalysis.getPreviousOnlyPanelMarkers(
      currentMarkers,
      getPreviousSectionMarkers(previousBloodworkAnalysis!),
    );
    return previousOnly.length
      ? [...currentMarkers, ...previousOnly]
      : currentMarkers;
  };
}

/**
 * True when at least one of these markers has a CURRENT value.
 *
 * False means the list is nothing but previous-only markers, so anything built
 * from the current test would be built from blanks. See insightsSourceAnalysis
 * for what the panels do about that.
 */
export function hasCurrentValues(panelMarkers: PanelMarker[]): boolean {
  return panelMarkers.some(panelMarker => panelMarker.hasValue());
}

/**
 * Which analysis a section's insights panel should be built from.
 *
 * Normally the current one. For a section this test has no results for at all
 * it is the PREVIOUS analysis, so the practitioner still gets the gauge and
 * findings for the collection those bars came from rather than a blank panel.
 * Every caller that renders such a panel must label it with that collection's
 * date (previousCollectionLabel) — a conclusion printed in a report headed by
 * today's collection date is read as being about today.
 *
 * `panelMarkers` is the DISPLAYED list, which is not the same question: the
 * Immune and Inflammation "Section Signals Only" toggles drop optimal markers
 * from it, so a section this test does have results for can display nothing
 * but previous-only rows. That is a filtered view, not an absent section, and
 * its panel stays the current one — hence the second check against the
 * section's own getter.
 *
 * undefined when there is nothing to build a panel from (no current results in
 * the section and no previous analysis), which means: render no panel at all.
 */
export function insightsSourceAnalysis(
  section: Section,
  panelMarkers: PanelMarker[],
  bloodworkAnalysis: BloodworkAnalysis,
): BloodworkAnalysis | undefined {
  if (hasCurrentValues(panelMarkers)) {
    return bloodworkAnalysis;
  }
  const getter = SECTION_PANEL_MARKER_GETTERS[section];
  const sectionMarkers: PanelMarker[] =
    getter && typeof (bloodworkAnalysis as any)[getter] === 'function'
      ? (bloodworkAnalysis as any)[getter]()
      : [];
  return hasCurrentValues(sectionMarkers)
    ? bloodworkAnalysis
    : bloodworkAnalysis.previousAnalysis;
}

/** MM/DD/YYYY, the format every other collection date in the report prints in. */
export function formatCollectionDate(collectionDate?: string): string {
  return collectionDate ? moment(collectionDate).format('MM/DD/YYYY') : '';
}

/**
 * Caption above an insights panel that was built from the previous collection.
 * Client-facing, so it states the fact and its date, nothing more.
 */
export function previousCollectionLabel(bloodwork?: Bloodwork): string {
  const date = formatCollectionDate(bloodwork?.collectionDate);
  return date
    ? `From the previous collection (${date})`
    : 'From the previous collection';
}

/**
 * What the caption costs the PDF page packer. The estimates in pdf-section.tsx
 * and the packer in bloodwork-analysis-pdf.tsx both add it, so a panel built
 * from the previous collection paginates like the current-test one plus its
 * label.
 */
export const PREVIOUS_COLLECTION_CAPTION_PDF_HEIGHT = 26;
